import {SizesCropInputs} from "./components/SizesCropInputs/SizesCropInputs"
import {Button} from "./components/Button/Button"
import {Checkbox} from "./components/Checkbox/Checkbox"
import {CropFrame} from "./components/CropFrame/CropFrame"
import {CustomFileInput} from "./components/CustomFileInput/CustomFileInput"
import {ModeSwitcher} from "./components/ModeSwitcher/ModeSwitcher"
import {VideoControlButton} from "./components/VideoControlButton/VideoControlButton"
import {ZoomSlider} from "./components/ZoomSlider/ZoomSlider"
import styles from "./Cropper.module.css"
import type {Dimensions} from "./types/types"
import {useCropperLogic} from "./hooks/clients/useCropperLogic"
import {useVideoControls} from "./hooks/clients/useVideoControls"

export interface CropperProps {
    materialIndex: number
    cropDimensions?: Dimensions
    file?: File

    // UI toggles
    showFileInput?: boolean
    showSizeInputs?: boolean
    showModeSwitcher?: boolean
    showZoom?: boolean
    showVideoControls?: boolean
    showProcessingControls?: boolean
    showMuteControl?: boolean

    // Styling
    className?: string
    cropFrameClassName?: string
    zoomSliderClassName?: string
    controlsClassName?: string
    style?: React.CSSProperties
}

export const Cropper = ({
    materialIndex,

    // UI toggles
    showFileInput = false,
    showSizeInputs = false,
    showModeSwitcher = false,
    showZoom = false,
    showVideoControls = false,
    showProcessingControls = false,
    showMuteControl = false,

    // Styling
    className,
    cropFrameClassName,
    zoomSliderClassName,
    controlsClassName,
    style,
}: CropperProps) => {
    // Деструктурируем cropperLogic для удобной передачи в компоненты
    const {
        // State
        imageState,
        cropState,
        isProcessing,
        downloadUrl,
        uploadProgress,
        mode,

        // Refs
        mainCanvasRef,

        // Calculations
        minZoom,
        maxZoom,
        step,

        // Event handlers
        handleImageUpload,
        handleModeSwitch,
        handleDragStart,
        handleDrag,
        handleDragEnd,
        handleMouseLeave,
        handleZoomChange,
        handleDimensionChange,
        processVideo,
        downloadProcessedVideo,
    } = useCropperLogic(materialIndex)

    const videoControls = useVideoControls(materialIndex)

    const isVideoDisabled = videoControls.isVideoDisabled(imageState)

    return (
        <div
            className={`${className} ${styles["cropper-container"]}`}
            style={style}
        >
            {showFileInput && (
                <CustomFileInput
                    handleFileChange={handleImageUpload}
                    disabled={isVideoDisabled}
                />
            )}

            {imageState.status === "loaded" ? (
                <>
                    {showModeSwitcher && !imageState.videoUrl && (
                        <ModeSwitcher
                            onModeChange={handleModeSwitch}
                            disabled={isVideoDisabled}
                        />
                    )}

                    <CropFrame
                        className={cropFrameClassName}
                        canvasRef={mainCanvasRef}
                        onDragStart={handleDragStart}
                        onDrag={handleDrag}
                        onDragEnd={handleDragEnd}
                        disabled={isVideoDisabled}
                        onMouseLeave={handleMouseLeave}
                    />

                    {showZoom && (
                        <ZoomSlider
                            className={zoomSliderClassName}
                            onChange={handleZoomChange}
                            disabled={isVideoDisabled}
                            value={imageState.zoom}
                            min={minZoom}
                            max={maxZoom}
                            step={step}
                            mode={mode}
                        />
                    )}

                    {imageState.videoUrl && (
                        <>
                            {showMuteControl && (
                                <Checkbox
                                    checked={videoControls.isMuted}
                                    children={<p>Mute sound</p>}
                                    onChange={videoControls.toggleMute}
                                    className={styles.checkbox}
                                />
                            )}

                            {showVideoControls && (
                                <VideoControlButton
                                    isPlaying={videoControls.isVideoPlaying}
                                    onClick={videoControls.toggleVideoPlaying}
                                />
                            )}

                            {showProcessingControls && (
                                <>
                                    <Button
                                        onClick={processVideo}
                                        disabled={isProcessing}
                                    >
                                        {isProcessing
                                            ? "Processing..."
                                            : "Process Video"}
                                    </Button>

                                    {downloadUrl && (
                                        <button
                                            onClick={() =>
                                                downloadProcessedVideo(
                                                    downloadUrl
                                                )
                                            }
                                            style={{
                                                padding:
                                                    "var(--spacing-8, 8px) var(--spacing-16, 16px)",
                                                backgroundColor:
                                                    "var(--grey-15, #e5e7eb)",
                                                color: "var(--main-opposite, #fff)",
                                                border: "none",
                                                borderRadius:
                                                    "var(--spacing-6, 6px)",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Download Processed Video
                                        </button>
                                    )}

                                    {isProcessing && (
                                        <div
                                            style={{
                                                width: "100%",
                                                height: "4px",
                                                backgroundColor:
                                                    "var(--grey-15, #e5e7eb)",
                                                borderRadius: "2px",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: `${uploadProgress}%`,
                                                    height: "100%",
                                                    backgroundColor:
                                                        "var(--grey-25, #d1d5db)",
                                                    borderRadius: "2px",
                                                    transition: "width 0.3s",
                                                }}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {showSizeInputs && (
                        <div className={controlsClassName}>
                            <SizesCropInputs
                                onSizeChange={handleDimensionChange}
                                cropState={cropState.dimensions}
                                disabled={isVideoDisabled}
                            />
                        </div>
                    )}
                </>
            ) : (
                <p className={styles["file-size-alert"]}>
                    Up to 2MB (image), 5MB (video)
                </p>
            )}
        </div>
    )
}

export default Cropper
